
import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { storageService } from '../services/storageService';

let supabaseInstance: SupabaseClient | null = null;

const isValidUrl = (url: string) => {
  try {
    new URL(url);
    return true;
  } catch (e) {
    return false;
  }
};

const getEnvVars = () => {
  // Try to get variables from process.env (injected by Vite define)
  // or fall back to the manual config stored in the app's settings
  const config = storageService.getConfig();
  
  const url = (typeof process !== 'undefined' && process.env?.SUPABASE_URL) 
    ? process.env.SUPABASE_URL 
    : config.supabaseUrl;
    
  const key = (typeof process !== 'undefined' && process.env?.SUPABASE_ANON_KEY) 
    ? process.env.SUPABASE_ANON_KEY 
    : config.supabaseAnonKey;

  return { url: url || '', key: key || '' };
};

/**
 * Checks if the required Supabase configuration is present.
 */
export const isSupabaseConfigured = (): boolean => {
  const { url, key } = getEnvVars();
  return !!(url && isValidUrl(url) && key);
};

/**
 * Returns a Supabase client instance or null if not configured.
 */
export const getSupabase = (): SupabaseClient | null => {
  if (supabaseInstance) return supabaseInstance;

  const { url, key } = getEnvVars();

  if (!url || !isValidUrl(url) || !key) {
    return null;
  }

  supabaseInstance = createClient(url, key, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    }
  });
  return supabaseInstance;
};

export const getTableName = () => storageService.getConfig().supabaseTableName || 'AI Events_OneSource Cloud';

/**
 * Auth accessor proxy - handles null client gracefully
 */
export const supabaseAuth = {
  getUser: () => {
    const client = getSupabase();
    if (!client) throw new Error("Supabase not configured");
    return client.auth.getUser();
  },
  getSession: () => {
    const client = getSupabase();
    if (!client) throw new Error("Supabase not configured");
    return client.auth.getSession();
  },
  getAccessToken: async (): Promise<string | null> => {
    const client = getSupabase();
    if (!client) return null;
    const { data } = await client.auth.getSession();
    return data.session?.access_token || null;
  },
  onAuthStateChange: (callback: any) => {
    const client = getSupabase();
    if (!client) return { data: { subscription: { unsubscribe: () => {} } } };
    return client.auth.onAuthStateChange(callback);
  },
  signInWithPassword: (credentials: any) => {
    const client = getSupabase();
    if (!client) throw new Error("Supabase not configured");
    return client.auth.signInWithPassword(credentials);
  },
  signUp: (credentials: any) => {
    const client = getSupabase();
    if (!client) throw new Error("Supabase not configured");
    return client.auth.signUp(credentials);
  },
  signOut: () => {
    const client = getSupabase();
    if (!client) return Promise.resolve();
    return client.auth.signOut();
  },
};

/**
 * Database accessor proxy
 */
export const supabaseDb = {
  from: (table: string) => {
    const client = getSupabase();
    if (!client) throw new Error("Supabase not configured");
    return client.from(table);
  }
};
